import type {ReactElement} from 'react';
import {StyleTile} from '@Components/style-tile/style-tile';
import {BrandColorType, BrandImageType, BrandKitSource} from '@Components/mystuff-brands/brand-kits.types';
import {repoImageThumbURL} from '@Libraries/graphic-item-image-library';
import {STYLE_TILE_COLORS_LIMIT} from '@Components/style-tile/style-tile.types';
import type {BrandKitStyleTileProps} from './brand-kit-style-tile.types';
import {openAppropriateUpsellingDialogForBrandKits, shouldUpsellHalloweenBrandKit, trackHalloweenBrandKitUsageIfApplicable} from '@Libraries/brands-library';

export function BrandKitStyleTile({brandKit, brandColors, brandLogos, brandFonts, onClickBrandKit, showCursorPointer = true, ...props}: BrandKitStyleTileProps): ReactElement<any> {
  const getLogoURL = (): string | undefined => {
    if (brandLogos.length === 0) {
      return undefined;
    }

    const logoToUse =
      brandLogos.find((brandLogo) => {
        return brandLogo.brandImageType === BrandImageType.PRIMARY_LOGO;
      }) ?? brandLogos[0];

    return repoImageThumbURL(logoToUse.filename, logoToUse.ext);
  };

  /**
   * creates the list of brand colors in the following order: Primary, Secondary, Other
   * adds 'other' type colors if the first two aren't available
   * Adds the hex '# to the start since brand color hashes aren't stored with the # character in the DB
   */
  const getColors = (): string[] => {
    let colorsLeftToAdd = STYLE_TILE_COLORS_LIMIT;
    const colorsToUse = [];
    const primaryColor = brandColors.find((brandColor) => {
      return brandColor.type === BrandColorType.PRIMARY;
    });

    if (primaryColor) {
      colorsLeftToAdd -= 1;
      colorsToUse.push(primaryColor);
    }

    const secondaryColor = brandColors.find((brandColor) => {
      return brandColor.type === BrandColorType.SECONDARY;
    });

    if (secondaryColor) {
      colorsLeftToAdd -= 1;
      colorsToUse.push(secondaryColor);
    }

    const otherColors = brandColors
      .filter((brandColor) => {
        return brandColor.type === BrandColorType.OTHER;
      })
      .slice(0, colorsLeftToAdd);

    colorsToUse.push(...otherColors);

    return colorsToUse.map((colorToUse) => {
      return `#${colorToUse.hexCode}`;
    });
  };

  const onClick = (): void => {
    trackHalloweenBrandKitUsageIfApplicable(brandKit.brandId);
    if (shouldUpsellHalloweenBrandKit(brandKit.brandId)) {
      openAppropriateUpsellingDialogForBrandKits();
      return;
    }

    onClickBrandKit(brandKit.brandId, getColors(), getLogoURL(), brandFonts[0]?.fontFamily);
  };

  return (
    <StyleTile
      onClick={onClick}
      imageURL={getLogoURL()}
      colorHexes={getColors()}
      fontFamily={brandFonts[0]?.fontFamily}
      tileText={brandKit.name}
      tileTextClasName={brandKit.source === BrandKitSource.HALLOWEEN_PROMOTION ? 'content-halloween' : ''}
      isSelected={props.isSelected}
      showCursorPointer={showCursorPointer}
      {...props}
    />
  );
}
